/* eslint-disable relay/unused-fields */
import { useAsync } from "@react-hookz/web";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { data as appData } from "shared/data";

import { useMyNews } from "scmp-app/lib/hooks";
import type {
  contextMynewsFeedViewQuery,
  contextMynewsFeedViewQuery$data,
  contextMynewsFeedViewQuery$variables,
} from "scmp-app/queries/__generated__/contextMynewsFeedViewQuery.graphql";

import type { UserFollowedItem } from "./type";

type ContextValue = {
  followedList: UserFollowedItem[];
  selectedItem: UserFollowedItem;
  setSelectedItem: (item: UserFollowedItem) => void;
};

const MynewsFeedViewContext = createContext<ContextValue>({
  followedList: [],
  selectedItem: { __typename: "all", entityId: "", entityUuid: "", name: "All" },
  setSelectedItem: () => {},
});

export type Props = {
  children?: ReactNode;
};

export const MynewsFeedViewProvider: React.FunctionComponent<Props> = ({ children }) => {
  const environment = useRelayEnvironment();
  const { followedIds, hasEnoughFollowedItems } = useMyNews();
  const [selectedItem, setSelectedItem] = useState<UserFollowedItem>({
    __typename: "all",
    entityId: "",
    entityUuid: "",
    name: "All",
  });

  const [{ result }, { execute }] = useAsync(async () => {
    try {
      const followedList = await fetchQuery<contextMynewsFeedViewQuery>(
        environment,
        graphql`
          query contextMynewsFeedViewQuery(
            $authorIds: [String]!
            $topicIds: [String]!
            $sectionIds: [String]!
            $hasAuthors: Boolean!
            $hasTopics: Boolean!
            $hasSections: Boolean!
            $first: Int!
            $applicationId: String!
          ) {
            authors(filter: { entityIds: $authorIds }, first: $first) @include(if: $hasAuthors) {
              edges {
                node {
                  entityId
                  name
                  entityUuid
                  __typename
                }
              }
            }
            topics(filter: { entityIds: $topicIds }, first: $first, applicationId: $applicationId)
              @include(if: $hasTopics) {
              edges {
                node {
                  entityId
                  name
                  entityUuid
                  __typename
                }
              }
            }
            sections(filter: { entityIds: $sectionIds }, first: $first) @include(if: $hasSections) {
              edges {
                node {
                  entityId
                  name
                  entityUuid
                  __typename
                }
              }
            }
          }
        `,
        {
          ...followedIds,
          applicationId: appData.application.scmp.entityId,
          first: 30,
          hasAuthors: followedIds.authorIds.length > 0,
          hasSections: followedIds.sectionIds.length > 0,
          hasTopics: followedIds.topicIds.length > 0,
        } as contextMynewsFeedViewQuery$variables,
        { fetchPolicy: "store-or-network" },
      ).toPromise();

      return followedList as contextMynewsFeedViewQuery$data;
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (hasEnoughFollowedItems) {
      void execute();
    }
  }, [hasEnoughFollowedItems, execute, followedIds]);

  const sortedFollowedList = useMemo(
    () => [
      ...(result?.topics?.edges?.map(({ node }) => node) ?? []),
      ...(result?.authors?.edges?.map(({ node }) => node) ?? []),
      ...(result?.sections?.edges?.map(({ node }) => node) ?? []),
    ],
    [result],
  );

  return (
    <MynewsFeedViewContext.Provider
      value={{
        followedList: sortedFollowedList ?? [],
        selectedItem,
        setSelectedItem,
      }}
    >
      {children}
    </MynewsFeedViewContext.Provider>
  );
};
MynewsFeedViewProvider.displayName = "MynewsFeedViewProvider";

export const useMynewsFeedViewContext = () => useContext(MynewsFeedViewContext);
